import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS } from '../utils/Constants';
import { GameState } from '../game/GameState';

export interface HUDSettings {
  showPointValues: boolean;
  showCardCounters: boolean;
  showTurnIndicator: boolean;
}

export class HUD {
  private scene: Phaser.Scene;
  private scoreText!: Phaser.GameObjects.Text;
  private aiScoreText!: Phaser.GameObjects.Text;
  private turnText!: Phaser.GameObjects.Text;
  private roundText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private aiCardCount!: Phaser.GameObjects.Text;
  private playerCardCount!: Phaser.GameObjects.Text;
  private rulesBtn!: Phaser.GameObjects.Text;
  private settingsBtn!: Phaser.GameObjects.Text;
  private messageText!: Phaser.GameObjects.Text;
  private messageTimer?: Phaser.Time.TimerEvent;
  private settings: HUDSettings = {
    showPointValues: true,
    showCardCounters: true,
    showTurnIndicator: true,
  };

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    // Left panel - scores
    this.scoreText = this.scene.add.text(15, GAME_HEIGHT / 2 - 30, 'You: 0', {
      fontSize: '16px',
      color: '#2ecc71',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setDepth(200);

    this.aiScoreText = this.scene.add.text(15, GAME_HEIGHT / 2 - 10, 'AI: 0', {
      fontSize: '16px',
      color: '#e74c3c',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setDepth(200);

    // Round number
    this.roundText = this.scene.add.text(15, GAME_HEIGHT / 2 + 15, 'Round 1', {
      fontSize: '14px',
      color: '#aaaaaa',
      fontFamily: 'Georgia, serif',
    }).setDepth(200);

    // Turn indicator (left of the deck/discard area)
    this.turnText = this.scene.add.text(GAME_WIDTH / 2 - 130, GAME_HEIGHT / 2 - 15, '', {
      fontSize: '18px',
      color: '#f1c40f',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5).setDepth(200);

    // Phase indicator (below turn indicator)
    this.phaseText = this.scene.add.text(GAME_WIDTH / 2 - 130, GAME_HEIGHT / 2 + 10, '', {
      fontSize: '14px',
      color: '#aaaaaa',
      fontFamily: 'Georgia, serif',
    }).setOrigin(1, 0.5).setDepth(200);

    // Card counters (centered, near each hand)
    this.aiCardCount = this.scene.add.text(GAME_WIDTH / 2, 20, '', {
      fontSize: '13px',
      color: '#aaaaaa',
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5, 1).setDepth(200);

    this.playerCardCount = this.scene.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 18, '', {
      fontSize: '13px',
      color: '#aaaaaa',
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5, 0).setDepth(200);

    // Rules button (top right)
    this.rulesBtn = this.scene.add.text(GAME_WIDTH - 15, 15, 'Rules', {
      fontSize: '16px',
      color: '#ffcc00',
      fontFamily: 'Georgia, serif',
    }).setOrigin(1, 0).setDepth(200)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.scene.scene.pause();
        this.scene.scene.launch('RulesScene', { returnTo: 'GameScene' });
      })
      .on('pointerover', () => this.rulesBtn.setColor('#ffffff'))
      .on('pointerout', () => this.rulesBtn.setColor('#ffcc00'));

    // Settings button
    this.settingsBtn = this.scene.add.text(GAME_WIDTH - 80, 15, 'Settings', {
      fontSize: '16px',
      color: '#ffcc00',
      fontFamily: 'Georgia, serif',
    }).setOrigin(1, 0).setDepth(200)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleSettings())
      .on('pointerover', () => this.settingsBtn.setColor('#ffffff'))
      .on('pointerout', () => this.settingsBtn.setColor('#ffcc00'));

    // Message area (center of screen)
    this.messageText = this.scene.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
      backgroundColor: 'rgba(0,0,0,0.7)',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setDepth(300).setAlpha(0);
  }

  update(state: GameState): void {
    this.scoreText.setText(`You: ${state.players[0].totalScore}`);
    this.aiScoreText.setText(`AI: ${state.players[1].totalScore}`);
    this.roundText.setText(`Round ${state.roundNumber}`);

    if (this.settings.showTurnIndicator) {
      const isHuman = state.currentPlayerIndex === 0;
      this.turnText.setText(isHuman ? 'Your Turn' : "AI's Turn");
      this.turnText.setColor(isHuman ? '#2ecc71' : '#e74c3c');
      this.turnText.setVisible(true);
    } else {
      this.turnText.setVisible(false);
    }

    // Phase
    const phaseNames: Record<string, string> = {
      draw: 'Draw a card',
      meld: 'Play melds or discard',
      discard: 'Discard a card',
      round_end: 'Round over',
      game_over: 'Game over',
    };
    if (state.currentPlayerIndex === 0) {
      this.phaseText.setText(phaseNames[state.turnPhase] || '');
      this.phaseText.setVisible(true);
    } else {
      this.phaseText.setVisible(false);
    }

    if (this.settings.showCardCounters) {
      this.aiCardCount.setText(`AI: ${state.players[1].hand.length} cards`);
      this.playerCardCount.setText(`You: ${state.players[0].hand.length} cards`);
      this.aiCardCount.setVisible(true);
      this.playerCardCount.setVisible(true);
    } else {
      this.aiCardCount.setVisible(false);
      this.playerCardCount.setVisible(false);
    }
  }

  showMessage(text: string, duration = 2000): void {
    if (this.messageTimer) this.messageTimer.destroy();

    this.messageText.setText(text);
    this.messageText.setAlpha(1);

    this.messageTimer = this.scene.time.delayedCall(duration, () => {
      this.scene.tweens.add({
        targets: this.messageText,
        alpha: 0,
        duration: 500,
      });
    });
  }

  private settingsPanel: Phaser.GameObjects.Container | null = null;
  private onSettingsApply?: () => void;

  setOnSettingsApply(callback: () => void): void {
    this.onSettingsApply = callback;
  }

  private toggleSettings(): void {
    if (this.settingsPanel) {
      this.settingsPanel.destroy();
      this.settingsPanel = null;
      return;
    }

    const panelW = 280;
    const panelH = 240;
    const panelX = GAME_WIDTH - panelW - 10;
    const panelY = 45;

    this.settingsPanel = this.scene.add.container(panelX, panelY).setDepth(400);

    const bg = this.scene.add.rectangle(0, 0, panelW, panelH, COLORS.UI_BG, 0.95)
      .setOrigin(0, 0).setStrokeStyle(2, COLORS.UI_BORDER);
    this.settingsPanel.add(bg);

    const title = this.scene.add.text(panelW / 2, 15, 'Settings', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.settingsPanel.add(title);

    const toggles: { label: string; key: keyof HUDSettings; y: number }[] = [
      { label: 'Show card counters', key: 'showCardCounters', y: 55 },
      { label: 'Show turn indicator', key: 'showTurnIndicator', y: 95 },
      { label: 'Show point values', key: 'showPointValues', y: 135 },
    ];

    for (const toggle of toggles) {
      const label = this.scene.add.text(15, toggle.y, toggle.label, {
        fontSize: '14px',
        color: '#cccccc',
        fontFamily: 'Georgia, serif',
      });
      this.settingsPanel.add(label);

      const isOn = this.settings[toggle.key];
      const btn = this.scene.add.text(panelW - 20, toggle.y, isOn ? 'ON' : 'OFF', {
        fontSize: '14px',
        color: isOn ? '#2ecc71' : '#e74c3c',
        fontFamily: 'Georgia, serif',
        fontStyle: 'bold',
      }).setOrigin(1, 0)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          this.settings[toggle.key] = !this.settings[toggle.key];
          btn.setText(this.settings[toggle.key] ? 'ON' : 'OFF');
          btn.setColor(this.settings[toggle.key] ? '#2ecc71' : '#e74c3c');
        });
      this.settingsPanel.add(btn);
    }

    // Apply button
    const applyBg = this.scene.add.rectangle(panelW / 2, panelH - 30, 100, 32, COLORS.BUTTON, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => applyBg.setFillStyle(COLORS.BUTTON_HOVER, 1))
      .on('pointerout', () => applyBg.setFillStyle(COLORS.BUTTON, 0.9))
      .on('pointerdown', () => {
        if (this.onSettingsApply) this.onSettingsApply();
        if (this.settingsPanel) {
          this.settingsPanel.destroy();
          this.settingsPanel = null;
        }
      });
    this.settingsPanel.add(applyBg);

    const applyText = this.scene.add.text(panelW / 2, panelH - 30, 'Apply', {
      fontSize: '14px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.settingsPanel.add(applyText);
  }

  getSettings(): HUDSettings {
    return this.settings;
  }

  destroy(): void {
    if (this.settingsPanel) {
      this.settingsPanel.destroy();
      this.settingsPanel = null;
    }
  }
}
